/* Pure helpers behind the Stats tab: the PR wall, the progress chart with its
 * program bands, the lift status board and weekly sets per muscle. The maths
 * lives in ../logic (shared with the MCP server); this file only picks the
 * inputs and shapes the results for the cards. Nothing here reads the clock:
 * the tab passes todayKey() in. */

import { completedSets } from '../logic/records';
import { liftTrend, sessionSeries } from '../logic/trends';
import { activeProgram } from '../logic/progression';
import { mondayOf } from '../logic/schedule';
import {
  VOLUME_GROUPS, plannedWeeklySets, resolveMuscles, volumeLandmarks, volumeZone, weeklyHardSets,
} from '../logic/muscles';

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Days since the epoch for a real YYYY-MM-DD key, else null. */
export function dayNum(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}

/** YYYY-MM-DD for a day number from dayNum. */
export function dayKey(n) {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

/** The date key `n` days after `key` (negative goes back), or null. */
export function addDays(key, n) {
  const d = dayNum(key);
  return d === null ? null : dayKey(d + n);
}

/** '3 Oct' for a date key (UTC, so the day never shifts with the device zone). */
export function shortDate(key) {
  const n = dayNum(key);
  if (n === null) return '';
  return new Date(n * 86400000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

const WEEKDAYS = ['Thu', 'Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed']; // day 0 was a Thursday

/** 'Fri 3 Oct' for a date key (built by hand: ICU versions differ on the comma). */
export function dayLabel(key) {
  const n = dayNum(key);
  if (n === null) return '';
  return `${WEEKDAYS[((n % 7) + 7) % 7]} ${shortDate(key)}`;
}

/** 6 -> '6', 6.13 -> '6.1'. */
export function fmtSets(v) {
  const r = Math.round((Number(v) || 0) * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/* ── PR wall ─────────────────────────────────────────────────────────────── */

export const PR_LABELS = { e1rm: 'e1RM PR', weight: 'Weight PR', reps: 'Rep PR' };

/**
 * PR events (buildRecords().events) in this calendar month and the one before.
 * @param {Array<{date:string}>} events
 * @param {string} today YYYY-MM-DD
 * @returns {{thisMonth:number, lastMonth:number}}
 */
export function prMonthCounts(events, today) {
  const out = { thisMonth: 0, lastMonth: 0 };
  if (!Array.isArray(events) || dayNum(today) === null) return out;
  const [y, m] = today.split('-').map(Number);
  const month = today.slice(0, 7);
  const prev = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
  events.forEach(e => {
    const k = typeof e?.date === 'string' ? e.date.slice(0, 7) : '';
    if (k === month) out.thisMonth += 1;
    else if (k === prev) out.lastMonth += 1;
  });
  return out;
}

/** Newest PR events first (buildRecords returns them oldest first). */
export function recentPrs(events, limit = 12) {
  return Array.isArray(events) ? events.slice(-limit).reverse() : [];
}

const OTHER = 'Other';

/**
 * Records table rows grouped by primary muscle, in volume-group order with
 * Cardio and 'Other' (unresolved names) last. Within a group the strongest
 * e1RM comes first. Exercises not trained in the last `recentDays` are left
 * out unless showAll, so renamed or abandoned names don't bury the table.
 * @param {Array} table buildRecords().table
 * @param {object} exerciseMeta state.exerciseMeta
 * @param {{today:string, recentDays?:number, showAll?:boolean}} opts
 * @returns {{groups:Array<{muscle:string, rows:Array}>, olderCount:number}}
 */
export function recordGroups(table, exerciseMeta, { today, recentDays = 56, showAll = false } = {}) {
  const t = dayNum(today);
  const byMuscle = new Map();
  let olderCount = 0;
  (Array.isArray(table) ? table : []).forEach(row => {
    const last = dayNum(row.lastDate);
    const older = t !== null && last !== null && t - last >= recentDays;
    if (older) olderCount += 1;
    if (older && !showAll) return;
    const muscle = resolveMuscles(row.exercise, exerciseMeta)?.primary || OTHER;
    if (!byMuscle.has(muscle)) byMuscle.set(muscle, []);
    byMuscle.get(muscle).push(row);
  });
  const order = [...VOLUME_GROUPS, 'Cardio', OTHER];
  const strength = r => (r.bestE1rm ? r.bestE1rm.e1rm : -1);
  const groups = order.filter(m => byMuscle.has(m)).map(muscle => ({
    muscle,
    rows: byMuscle.get(muscle).slice().sort((a, b) => strength(b) - strength(a) || a.exercise.localeCompare(b.exercise)),
  }));
  return { groups, olderCount };
}

/* ── Progress chart ──────────────────────────────────────────────────────── */

/**
 * One point per session of an exercise for the progress chart, from the
 * shared sessionSeries (so the e1RM here is the same number Claude quotes).
 * @param {object} logs state.logs
 * @param {string} name exercise name
 * @param {{exerciseMeta?:object, events?:Array}} [opts] events: PR events,
 *   whose dates become pr: true (gold dots)
 * @returns {Array<{date, x, e1rm, topWeight, reps, volume, pr}>} x = dayNum(date)
 */
export function progressSeries(logs, name, { exerciseMeta, events } = {}) {
  if (!name) return [];
  const prDates = new Set((Array.isArray(events) ? events : []).filter(e => e.exercise === name).map(e => e.date));
  return sessionSeries(logs, name, { exerciseMeta }).map(p => ({
    date: p.date,
    x: dayNum(p.date),
    e1rm: p.e1rm,
    topWeight: p.topWeight,
    reps: p.topReps,
    volume: Math.round(p.volume),
    pr: prDates.has(p.date),
  }));
}

/**
 * Program blocks of one exercise clipped to the chart's x range [xMin, xMax]
 * (day numbers), oldest first, so a switch of bench program shows as bands.
 * @param {Array} programs state.programs
 * @param {string} exercise
 * @param {number} xMin
 * @param {number} xMax
 * @returns {Array<{id, name, startDate, endDate, x1, x2, startsInView, active}>}
 *   startsInView: the block starts after the first point, i.e. a visible switch
 */
export function programBands(programs, exercise, xMin, xMax) {
  if (!Array.isArray(programs) || typeof xMin !== 'number' || typeof xMax !== 'number') return [];
  const out = [];
  programs.forEach((b, i) => {
    if (!isObj(b) || b.exercise !== exercise) return;
    const start = dayNum(b.startDate);
    if (start === null) return;
    const end = b.endDate == null ? null : dayNum(b.endDate);
    // A block replaced on its own start day ends before it began: never ran.
    if (end !== null && end < start) return;
    const x1 = Math.max(start, xMin);
    const x2 = Math.min(end === null ? xMax : end, xMax);
    if (x1 > x2) return;
    out.push({
      id: typeof b.id === 'string' && b.id ? b.id : `block-${i}`,
      name: typeof b.name === 'string' && b.name.trim() ? b.name.trim() : 'Program',
      startDate: b.startDate,
      endDate: end === null ? null : b.endDate,
      x1,
      x2,
      startsInView: start > xMin,
      active: end === null,
    });
  });
  return out.sort((a, b) => a.x1 - b.x1);
}

/* ── Lift status ─────────────────────────────────────────────────────────── */

const STATUS_RANK = { regressing: 0, stalled: 1, progressing: 2, insufficient: 3 };

/** The chip a liftTrend result gets: its label, or why there is none. */
export function statusChip(trend) {
  if (!trend) return 'not enough data';
  if (trend.reason === 'assisted') return 'assisted';
  if (trend.status === 'insufficient') return 'not enough data';
  return trend.label;
}

/**
 * Every exercise with a counted set in the last `days` days (what Mo really
 * trains, swaps included), with its liftTrend. The trend starts at the active
 * program block, so a new rep scheme doesn't read as a drop, and lifts done
 * twice per cycle need 4 flat sessions instead of 3 before they stall.
 * @param {object} state needs logs; reads plan, programs, profile.goal, exerciseMeta
 * @param {string} today YYYY-MM-DD
 * @param {{days?:number}} [opts]
 * @returns {Array<{name, lastDate, trend, chip, text, program:{name, startDate}|null}>}
 *   flagged lifts first (regressing, stalled, progressing, then the rest),
 *   most recently trained first within a status
 */
export function liftStatusBoard(state, today, { days = 56 } = {}) {
  const t = dayNum(today);
  const logs = isObj(state?.logs) ? state.logs : {};
  if (t === null) return [];
  const seen = new Map();
  Object.keys(logs).sort().forEach(date => {
    const d = dayNum(date);
    if (d === null || d > t || t - d >= days || !isObj(logs[date])) return;
    Object.keys(logs[date]).forEach(name => {
      if (!completedSets(logs[date][name]).length) return;
      const info = seen.get(name) || { last: date, count: 0 };
      info.last = date;
      info.count += 1;
      seen.set(name, info);
    });
  });
  const plan = Array.isArray(state?.plan) ? state.plan : [];
  const rows = [...seen].map(([name, info]) => {
    const block = activeProgram(state?.programs, name, today, { plan });
    const planDays = plan.filter(day => isObj(day) && !day.isRest && Array.isArray(day.exercises)
      && day.exercises.some(ex => ex && ex.name === name)).length;
    // Not in the plan (a swap target): judge by how often it was logged.
    const perCycle = planDays || (plan.length ? (info.count * plan.length) / days : 1);
    const trend = liftTrend(logs, name, {
      today,
      goal: state?.profile?.goal,
      since: block ? block.startDate : undefined,
      stallSessions: perCycle >= 2 ? 4 : 3,
      exerciseMeta: state?.exerciseMeta,
    });
    const prefix = `${name}: `;
    const text = trend.sentence.startsWith(prefix) ? trend.sentence.slice(prefix.length) : trend.sentence;
    return {
      name,
      lastDate: info.last,
      trend,
      chip: statusChip(trend),
      text: text.charAt(0).toUpperCase() + text.slice(1),
      program: block ? { name: block.name || 'Program', startDate: block.startDate } : null,
    };
  });
  return rows.sort((a, b) => (STATUS_RANK[a.trend.status] ?? 9) - (STATUS_RANK[b.trend.status] ?? 9)
    || (a.lastDate < b.lastDate ? 1 : a.lastDate > b.lastDate ? -1 : 0)
    || a.name.localeCompare(b.name));
}

/* ── Weekly sets per muscle ──────────────────────────────────────────────── */

function firstLoggedDate(logs) {
  let first = null;
  Object.keys(logs).forEach(date => {
    if (dayNum(date) === null || (first !== null && date >= first) || !isObj(logs[date])) return;
    if (Object.values(logs[date]).some(sets => Array.isArray(sets) && sets.some(s => s && s.done))) first = date;
  });
  return first;
}

/**
 * Hard sets per muscle for this Mon-Sun week and the average of the full
 * weeks before it, plus the plan, landmarks and names that resolve to no
 * muscle (so Mo can assign them).
 * @param {object} state needs logs; reads plan, exerciseMeta, exerciseAliases, profile
 * @param {string} today YYYY-MM-DD
 * @param {{avgWeeks?:number}} [opts]
 * @returns {{weekStart:string, thisWeek:Object<string,number>, avg:Object<string,number>,
 *   avgWeeks:number, planned:Object<string,number>, landmarks:object, unmapped:string[]}}
 *   avgWeeks: full weeks averaged; weeks before the first logged session
 *   are left out, so a new user's average isn't dragged down by them
 */
export function muscleVolume(state, today, { avgWeeks = 4 } = {}) {
  const weekStart = mondayOf(today);
  const logs = isObj(state?.logs) ? state.logs : {};
  const thisWeek = weekStart ? weeklyHardSets(state, weekStart) : { byMuscle: {}, unmapped: {} };
  const first = firstLoggedDate(logs);
  const firstMonday = first ? mondayOf(first) : null;
  const weeks = [];
  for (let i = 1; weekStart && firstMonday && i <= avgWeeks; i += 1) {
    const ws = dayKey(dayNum(weekStart) - 7 * i);
    if (ws < firstMonday) break;
    weeks.push(weeklyHardSets(state, ws));
  }
  const sum = {};
  weeks.forEach(w => Object.entries(w.byMuscle).forEach(([m, v]) => { sum[m] = (sum[m] || 0) + v; }));
  const avg = {};
  Object.keys(sum).forEach(m => { avg[m] = Math.round((sum[m] / weeks.length) * 10) / 10; });
  const planned = plannedWeeklySets(state);
  const unmapped = new Set(Object.keys(thisWeek.unmapped));
  weeks.forEach(w => Object.keys(w.unmapped).forEach(n => unmapped.add(n)));
  Object.keys(planned.unmapped).forEach(n => unmapped.add(n));
  return {
    weekStart,
    thisWeek: thisWeek.byMuscle,
    avg,
    avgWeeks: weeks.length,
    planned: planned.byMuscle,
    landmarks: volumeLandmarks(state),
    unmapped: [...unmapped].sort((a, b) => a.localeCompare(b)),
  };
}

/**
 * Bar rows for the card: every volume group with sets, a plan or a non-zero
 * MEV (an untrained muscle that needs volume should still show).
 * @returns {Array<{muscle, sets, plan, landmark, zone}>}
 */
export function muscleRows(values, planned, landmarks) {
  return VOLUME_GROUPS.map(muscle => {
    const sets = Number(values?.[muscle]) || 0;
    const plan = Number(planned?.[muscle]) || 0;
    const landmark = landmarks?.[muscle] || null;
    return { muscle, sets, plan, landmark, zone: landmark ? volumeZone(sets, landmark) : null };
  }).filter(r => r.sets > 0 || r.plan > 0 || (r.landmark && r.landmark.mev > 0));
}

const EXAMPLES = {
  Chest: 'presses or flyes',
  Back: 'rows or pulldowns',
  'Front delts': 'overhead presses',
  'Side delts': 'lateral raises',
  'Rear delts': 'rear delt flyes',
  Biceps: 'curls',
  Triceps: 'pushdowns',
  Forearms: 'wrist curls',
  Quads: 'leg extensions',
  Hamstrings: 'leg curls',
  Glutes: 'hip thrusts',
  Calves: 'calf raises',
  Core: 'crunches',
};

const VERDICT_SKIP = new Set(['Forearms']);

/* The plan exercise that trains a muscle most often, so the advice names
 * something Mo already does. */
function planExerciseFor(state, muscle) {
  const counts = new Map();
  (Array.isArray(state?.plan) ? state.plan : []).forEach(day => {
    if (!isObj(day) || day.isRest || !Array.isArray(day.exercises)) return;
    day.exercises.forEach(ex => {
      if (!ex || typeof ex.name !== 'string') return;
      if (resolveMuscles(ex.name, state.exerciseMeta)?.primary !== muscle) return;
      counts.set(ex.name, (counts.get(ex.name) || 0) + 1);
    });
  });
  let best = null;
  counts.forEach((n, name) => { if (!best || n > best.n) best = { name, n }; });
  return best ? best.name : null;
}

/**
 * One line about the muscle furthest outside its rough range (relative to
 * the landmark; Forearms are left out), e.g. 'Side delts 4/wk, below 8: add ~4 sets a week of
 * Lateral Raises.'
 * @param {Object<string,number>} values sets per muscle per week
 * @param {object} landmarks volumeLandmarks()
 * @param {object} state reads plan and exerciseMeta for the example exercise
 * @returns {{text:string, muscle:string|null, zone:'below'|'over'|null, others:number}}
 */
export function volumeVerdict(values, landmarks, state) {
  let worst = null;
  let outside = 0;
  VOLUME_GROUPS.forEach(muscle => {
    // Forearms get their work from pulling and grip, which isn't credited;
    // "add wrist curls" would top the list for nearly every PPL lifter.
    if (VERDICT_SKIP.has(muscle)) return;
    const lm = landmarks?.[muscle];
    if (!lm) return;
    const v = Number(values?.[muscle]) || 0;
    let severity = 0;
    let zone = null;
    if (lm.mev > 0 && v < lm.mev) { severity = (lm.mev - v) / lm.mev; zone = 'below'; }
    else if (lm.mrv > 0 && v > lm.mrv) { severity = (v - lm.mrv) / lm.mrv; zone = 'over'; }
    if (!zone) return;
    outside += 1;
    if (!worst || severity > worst.severity) worst = { muscle, v, lm, severity, zone };
  });
  if (!worst) return { text: 'Every muscle is within its rough range.', muscle: null, zone: null, others: 0 };
  const { muscle, v, lm, zone } = worst;
  const others = outside - 1;
  const more = others ? ` (${others} more outside ${others === 1 ? 'its' : 'their'} range)` : '';
  if (zone === 'below') {
    const gap = Math.max(1, Math.ceil(lm.mev - v - 1e-9));
    const example = planExerciseFor(state, muscle) || EXAMPLES[muscle] || 'sets for it';
    return {
      text: `${muscle} ${fmtSets(v)}/wk, below ${lm.mev}: add ~${gap} ${gap === 1 ? 'set' : 'sets'} a week of ${example}.${more}`,
      muscle, zone, others,
    };
  }
  const gap = Math.max(1, Math.ceil(v - lm.mrv - 1e-9));
  return {
    text: `${muscle} ${fmtSets(v)}/wk, above ${lm.mrv}: trim ~${gap} ${gap === 1 ? 'set' : 'sets'} a week.${more}`,
    muscle, zone, others,
  };
}
